import React, { forwardRef, useEffect, useState } from "react";
import { LocationDataType } from "../MeetingRoomLocationFilter";
import { Container, DropdownContainer, SelectedContainer } from "./LocationFilterDropdown.styles";
import UI from "@mile-meeting/mile-ui";
import BuildingDropdown from "./BuildingDropdown/BuildingDropdown";
import { TBuilding } from "types/building.types";
import FloorDropdown from "./FloorDropdown/FloorDropdown";
import { FilterContainer } from "pages/meeting/my-booking/index.styles";

type LocationFilterDropdownProps = {
  onChange?: (value: LocationDataType[]) => void;
  open?: boolean;
  locationData: (TBuilding | null)[];
  filter?: LocationDataType[];
};

const LocationFilterDropdown = forwardRef<HTMLDivElement, LocationFilterDropdownProps>(
  ({ onChange, open, locationData, filter = [] }, ref) => {
    const [building, setBuilding] = useState<{ name: string; id: string } | null>();

    //* INFO: 선택된 필터 삭제
    const handleRemoveFilter = (index: number) => {
      onChange?.(filter.filter((_, i) => i !== index));
    };

    //* INFO: 층 값 string으로 변환
    const getFloorValueString = (floors?: { name?: string }[], buildingName?: string) => {
      if (buildingName === undefined || floors?.length === 0) {
        return "전체 층";
      } else {
        return floors?.map((item) => (item !== null ? item.name : "기타 (층 미지정)")).join(", ");
      }
    };

    //* INFO: 층 선택 시 필터링
    const handleChangeFloor = (floor: { name: string; id: string } | null) => {
      setBuilding(undefined);

      //* INFO: 전체 층 선택했을 때, floor 배열을 []로 초기화
      if (floor?.id === "") {
        onChange?.([{ building: building, floor: [] }]);
        return;
      }

      const isFloorAlreadyInFilter = filter.some(
        (data) =>
          (building === null ? data.building === null : data.building?.id === building?.id) &&
          (floor === null
            ? data.floor && data.floor.some((f) => f === null) // floor 배열에 null이 있는지 확인
            : data.floor && data.floor.some((f) => f?.id === floor?.id))
      );

      //* INFO: 이미 선택된 층이면 필터링하지 않음
      if (isFloorAlreadyInFilter) return;

      if (building?.name === "전체 건물") {
        onChange?.([{ building: undefined, floor: [] }]);
      } else {
        //* INFO: filter에 building이 이미 있을 경우, 해당 building에 floor 추가
        const buildingInFilter = filter.find((data) => data.building?.id === building?.id);

        if (buildingInFilter) {
          onChange?.(
            filter.map((data) => {
              if (data.building?.id === building?.id) {
                const floorData = new Set(data.floor);
                floorData.add(floor);

                return { ...data, floor: Array.from(floorData) };
              }
              return data;
            })
          );
        } else {
          if (building) {
            onChange?.([...filter, { building: building, floor: floor === undefined ? [] : [floor] }]);
          } else {
            //* INFO: 기타 (건물 미지정) & 기타 (층 미지정)
            onChange?.([...filter, { building: null, floor: [null] }]);
          }
        }
      }
    };

    useEffect(() => {
      if (open) {
        setBuilding(undefined);
      }
    }, [open]);

    return (
      <Container style={{ display: open ? "block" : "none" }} ref={ref}>
        <SelectedContainer>
          {filter.map((data, index) => {
            const building_filter = data.building === null ? "기타 (건물 미지정)" : data.building?.name ?? "전체 건물";
            const tagValue = `${building_filter}
                ${data.building === null ? "-" : data.building?.name !== undefined ? "-" : ""} 
                ${getFloorValueString(
                  data.floor?.map((item) => ({ name: item?.name })),
                  data.building?.name
                )}`;

            return (
              <UI.Chip.Contained
                onClick={() => handleRemoveFilter(index)}
                key={index}
                title={tagValue}
                rightIcon="x"
                showRightIcon
                size="small"
                type="secondary"
              />
            );
          })}
        </SelectedContainer>
        <DropdownContainer>
          <BuildingDropdown
            selectedBuilding={building}
            buildingList={locationData}
            onChange={(value) => {
              setBuilding(value);

              if (value?.id === "") {
                //* INFO: 전체 건물 선택했을 때, 층 선택 안하고 바로 필터링
                onChange?.([{ building: { name: undefined }, floor: [] }]);
              }
            }}
          />
          <FloorDropdown locationData={locationData} building={building} onChange={handleChangeFloor} />
        </DropdownContainer>
      </Container>
    );
  }
);

export default LocationFilterDropdown;
